import React, { useEffect, useMemo, useState } from 'react';

import { Box, Container, Grid, Typography, makeStyles } from '@material-ui/core';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useSnackbar } from 'notistack';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

import CreateCaseForm from '../../components/createCaseForm/CreateCaseForm';
import ValidationException from '../../exceptions/ValidationException';
import { allCaseUploadOptions } from '../../models/Case';
import { WorkspaceProductType } from '../../models/Workspace';
import useBlocRef from '../../utils/useBlocRef.hook';

const messages = {
  complete: {
    hint:
      // eslint-disable-next-line max-len
      'Please provide the needed material to be able to provide your patient with a personalized video report. If the patient already exists on the system their details will automatically populate once their email address is provided',
    title: 'Complete Case',
  },
  create: {
    hint:
      // eslint-disable-next-line max-len
      'Please provide the needed material to be able to provide your patient with a personalized video report. If the patient already exists on the system their details will automatically populate once their email address is provided',
    title: 'Create Case Study',
  },
};

const useStyles = makeStyles((theme) => ({
  link: {
    '&:hover': {
      textDecoration: 'underline',
    },

    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '16px',
    color: '#FFF',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    fontSize: '14px',
    padding: '2px 6px',
    textDecoration: 'none',
    textTransform: 'capitalize',
  },
  linkContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '4px',
  },
  playIcon: {
    color: '#FFFFFF',
    height: '20px !important',
    marginLeft: '2px',
    width: '20px !important',
  },
}));

export default () => {
  const blocRef = useBlocRef('createCaseBloc');
  const blocRefUploadProgress = useBlocRef('uploadProgressBloc');

  const history = useHistory();
  const classes = useStyles();

  const [patientData, setPatientData] = useState({});
  const [status, setStatus] = useState({});
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [isMediphanyWorkspace, setIsMediphanyWorkspace] = useState(false);
  const [termsCheck, setTermsCheck] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [selectedAdminList, setSelectedAdminList] = useState([]);
  const [notifyMe, setNotifyMe] = useState();
  const [userDetails, setUserDetails] = useState({});
  const [discountData, setDiscountData] = useState({});
  const [redirectToCaseCreated, setRedirectToCaseCreated] = useState(false);
  const [workspaceDetail, setWorkspaceDetail] = useState({});

  const isConfirmRequestEnabled = useMemo(() => {
    if (status?.imageUploadOption === allCaseUploadOptions.imageUpload.value) {
      const statusFileType = status?.fileType || 'dcm';

      let isValid = status?.uploaded && status?.imageCount > 0;

      if (statusFileType !== 'dcm') isValid = status.uploaded;

      if (workspaceDetail?.radiologyReportRequired) {
        isValid = status.uploaded && status.imageCount > 0 && (status?.uploadedReport?.length || status?.hasReport);
        if (statusFileType !== 'dcm') isValid = status.uploaded && status.uploadedReport?.length > 0;
      }

      return isValid;
    }

    if (status?.imageUploadOption === allCaseUploadOptions.shareFolderLink.value) {
      const { onlineServiceName, sharingLink, studyDescription } = status.formOnlineFolderLink;

      return !!onlineServiceName && !!sharingLink && !!studyDescription;
    }

    if (status?.imageUploadOption === allCaseUploadOptions.mailToPOBox.value) {
      return true;
    }

    return (
      !!status?.formOnlinePortalAccess?.portalAddress &&
      !!status?.formOnlinePortalAccess?.portalPassword &&
      !!status?.formOnlinePortalAccess?.portalUsername &&
      !!status?.formOnlinePortalAccess?.studyName
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, workspaceDetail?.radiologyReportRequired]);

  const { enqueueSnackbar } = useSnackbar();
  const { search } = useLocation();
  const [validationErrors, setValidationErrors] = useState({});
  let { mode } = queryString.parse(search);
  mode = mode && messages[mode] ? mode : 'create';

  useEffect(() => {
    const additionalFilesSub = blocRef.current.additionalFiles$.subscribe(setAdditionalFiles);
    const patientDataSub = blocRef.current.patientData$.subscribe(setPatientData);
    const statusSub = blocRef.current.filesStatus$.subscribe(setStatus);
    const workspaceSub = blocRef.current.isMediphanyWorkspace$.subscribe(setIsMediphanyWorkspace);
    const termsCheckSub = blocRef.current.termsCheck$.subscribe(setTermsCheck);
    const adminListSub = blocRef.current.adminList$.subscribe(setAdminList);
    const selectedAdminListSub = blocRef.current.selectedAdminList$.subscribe(setSelectedAdminList);
    const notifyMeSub = blocRef.current.notifyMe$.subscribe(setNotifyMe);
    const userDetailsSub = blocRef.current.userDetails$.subscribe(setUserDetails);
    const discountDataSub = blocRef.current.discountData$.subscribe(setDiscountData);
    const redirectToCaseCreatedSub = blocRef.current.redirectToCaseCreated$.subscribe(setRedirectToCaseCreated);
    const workspaceDetailSub = blocRef.current.userWorkspace$.subscribe(setWorkspaceDetail);

    blocRef.current.handleGetWorkspaceDiscountDetail();

    return () => {
      additionalFilesSub.unsubscribe();
      patientDataSub.unsubscribe();
      statusSub.unsubscribe();
      workspaceSub.unsubscribe();
      termsCheckSub.unsubscribe();
      adminListSub.unsubscribe();
      notifyMeSub.unsubscribe();
      selectedAdminListSub.unsubscribe();
      userDetailsSub.unsubscribe();
      discountDataSub.unsubscribe();
      redirectToCaseCreatedSub.unsubscribe();
      workspaceDetailSub.unsubscribe();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      blocRef.current.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (redirectToCaseCreated) history.push('/case-created');
  }, [history, redirectToCaseCreated]);

  const handleFilesUpload = async (files, type) => {
    try {
      await blocRef.current.parseFiles(files, type);
    } catch (error) {
      enqueueSnackbar(error, { autoHideDuration: 10000, variant: 'error' });
    }
  };

  const handleOpenSelectImageUploadDialog = () => {
    blocRef.current.openSelectImageUploadDialogSelector();
  };

  const handleRemoveFiles = () => {
    blocRef.current.removeFiles();
  };

  const handleReportUpload = (files) => {
    blocRef.current.uploadReports(files);
  };

  const handleRemoveReport = () => {
    blocRef.current.removeReport();
  };

  const handleEmailChange = (email) => blocRef.current.getPatientByEmail(email);

  const handleCancelClick = () => {
    history.goBack();
  };

  const handleSubmitClick = async (data) => {
    try {
      await blocRef.current.sendCase(data, mode);

      history.push('/case-created');
    } catch (exception) {
      if (exception instanceof ValidationException) {
        enqueueSnackbar('Please fill all the required fields correctly', { autoHideDuration: 5000, variant: 'error' });
        setValidationErrors(exception.errors);
      }

      blocRefUploadProgress.current.close();
    }
  };

  const handleInvalidFiles = () => enqueueSnackbar('Please make sure all the required information have been filled out', { autoHideDuration: 10000, variant: 'error' });
  const handleOpenAdditionalFilesDialog = () => blocRef.current.openAdditionalFileDialog();
  const handleRemoveAdditionalFiles = (index) => blocRef.current.removeAdditionalFilesByIndex(index);
  const handleChangeImageUploadOption = (val) => blocRef.current.updateImageUploadOption(val);
  const handleChangeFormOnlinePortalAccess = (value, field, validForm) => blocRef.current.updateFormOnlinePortalAccess(value, field, validForm);
  const handleOnlineFolderLink = (value, field, validForm) => blocRef.current.updateFormOnlineFolderLink(value, field, validForm);
  const handleUpdateTermsCheck = (target, value) => blocRef.current.updateTermsCheck(target, value);
  const handleUpdateSelectedAdmins = (data) => blocRef.current.updateSelectedAdminList(data);
  const handleUpdateNotifyMe = (data) => blocRef.current.updateNotifyMe(data);
  const handleUpdateSelectedPackage = (selectedPackage) => blocRef.current.setSelectedPackage(selectedPackage);
  const handleUpdateSelectedPaymentMethod = (selectedPaymentMethod) => blocRef.current.setSelectedPaymentMethod(selectedPaymentMethod);
  const handleOpenUploadReportDialog = () => blocRef.current.openUploadReportDialog();

  return (
    <Grid component={Container} container className="mt-2">
      <Grid item className="text-left mt-1" xs={12} style={{ padding: '0 2rem' }}>
        <Typography className="page-header">Request Video Report</Typography>
        <Typography className="body-copy mt-0">Please complete the information below in order to place a request for a video report from our team.</Typography>
        <br />
        {workspaceDetail?.productType === WorkspaceProductType.LawFirm.value && (
          <Box className={classes.linkContainer}>
            <Typography className="body-copy mt-0">
              <div>Need help? For detailed instructions, watch the</div>
            </Typography>

            <div className={classes.link}>
              <PlayCircleOutlineIcon className={classes.playIcon} />
              <a href="https://youtu.be/MQe9INHFABw" target="_blank" rel="noopener noreferrer" title="Go to tutorial" className={classes.link}>
                Tutorial: How to order a video report
              </a>
            </div>
          </Box>
        )}
      </Grid>

      <Grid item>
        <CreateCaseForm
          mode={mode}
          patientData={patientData}
          filesStatus={status}
          onOpenUploadReportDialog={handleOpenUploadReportDialog}
          onFilesUpload={handleFilesUpload}
          onRemoveFiles={handleRemoveFiles}
          onReportUpload={handleReportUpload}
          onRemoveReport={handleRemoveReport}
          onEmailChange={handleEmailChange}
          onCancelClick={handleCancelClick}
          onSubmit={handleSubmitClick}
          onInvalidFiles={handleInvalidFiles}
          validationErrors={validationErrors}
          additionalFiles={additionalFiles}
          onOpenAdditionalFilesDialog={handleOpenAdditionalFilesDialog}
          onRemoveAdditionalFiles={handleRemoveAdditionalFiles}
          onImageUploadOptionChange={handleChangeImageUploadOption}
          onChangeFormOnlinePortalAccess={handleChangeFormOnlinePortalAccess}
          onChangeOnlineFolderLink={handleOnlineFolderLink}
          isSubmitting$={blocRef.current.isSubmitting$}
          termsCheck={termsCheck}
          onUpdateTermsCheck={handleUpdateTermsCheck}
          admins={adminList}
          onUpdateSelectedAdmins={handleUpdateSelectedAdmins}
          notifyMe={notifyMe}
          onUpdateNotifyMe={handleUpdateNotifyMe}
          selectedAdmins={selectedAdminList}
          selectedPackage={status.selectedPackage}
          onUpdateSelectedPackage={handleUpdateSelectedPackage}
          selectedPaymentMethod={status.selectedPaymentMethod}
          onUpdatePaymentMethod={handleUpdateSelectedPaymentMethod}
          userDetails={userDetails}
          discountData={discountData}
          workspaceDetail={workspaceDetail}
          onOpenSelectImageUploadDialog={handleOpenSelectImageUploadDialog}
          onButtonEnabled={isConfirmRequestEnabled}
        />
      </Grid>
    </Grid>
  );
};
